/**
 * Confirmation modal для звуження enabled_modules бізнес-шаблону
 * (Порція Виробництво.5, Етап 5).
 *
 * Використовується у двох сценаріях:
 *  1. PATCH /business-templates/{id} зі звуженими enabled_modules —
 *     показуємо скільки документів усіх orgs цього template'а буде
 *     приховано після PATCH.
 *  2. PATCH /organizations/{id} зі зміною business_template_id —
 *     те саме, але для однієї конкретної юрособи (organization_id
 *     передається у affected-docs).
 *
 * Українські назви через `moduleName()` + `docTableName()` —
 * фундаментальна вимога Етапу 4 (4 точки українізації).
 */

import { moduleName } from '@/utils/moduleNames';
import type { AffectedDocs } from '@/api/admin';

interface Props {
  open: boolean;
  /**
   * Префікс заголовку — «Звуження шаблону…» або «Перемикання юрособи…».
   * Підказка для контексту.
   */
  title: string;
  /** Модулі, що ВИЛУЧАЮТЬСЯ (raw codes). */
  removedModules: string[];
  /** Дані з GET /affected-docs. */
  affected: AffectedDocs | null;
  /** Loading-стан запиту affected-docs. */
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModuleNarrowingModal({
  open,
  title,
  removedModules,
  affected,
  isLoading,
  onConfirm,
  onCancel,
}: Props) {
  if (!open) return null;

  const total = affected?.total ?? 0;
  const byModule = affected?.by_module ?? {};

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-narrowing-title"
      style={{
        position: 'fixed', inset: 0,
        background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: 'var(--bg-panel)',
          color: 'var(--text)',
          borderRadius: 8,
          padding: '1.5rem',
          maxWidth: 560, width: '90%',
          maxHeight: '80vh', overflow: 'auto',
          boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
        }}
      >
        <h3 id="confirm-narrowing-title" style={{ marginTop: 0 }}>{title}</h3>

        <p>
          Ви видаляєте такі модулі з шаблону:{' '}
          <strong>
            {removedModules.map(moduleName).join(', ')}
          </strong>
        </p>

        {isLoading && <p>Підрахунок документів…</p>}

        {!isLoading && total === 0 && (
          <p style={{ color: 'var(--success, green)' }}>
            У жодній юрособі цього шаблону немає документів у цих модулях.
            Звуження безпечне.
          </p>
        )}

        {!isLoading && total > 0 && (
          <>
            <p style={{ color: 'var(--warning, #c70)' }}>
              <strong>{total}</strong>{' '}
              {pluralize(total, ['документ', 'документи', 'документів'])}{' '}
              стануть невидимими у меню для користувачів. Документи НЕ
              видаляються — вони лишаються у БД і повернуться у меню,
              якщо модулі знову увімкнути.
            </p>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
              <thead>
                <tr>
                  <th style={th}>Модуль</th>
                  <th style={{ ...th, textAlign: 'right' }}>Документів</th>
                </tr>
              </thead>
              <tbody>
                {removedModules.map((m) => (
                  <tr key={m}>
                    <td style={td}>{moduleName(m)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {byModule[m] ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} type="button" className="btn">
            Скасувати
          </button>
          <button
            onClick={onConfirm}
            type="button"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {total > 0 ? 'Все одно зберегти' : 'Підтвердити'}
          </button>
        </div>
      </div>
    </div>
  );
}

function pluralize(n: number, forms: [string, string, string]): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return forms[0];
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return forms[1];
  return forms[2];
}

const th: React.CSSProperties = {
  padding: '0.3rem 0.5rem',
  textAlign: 'left',
  borderBottom: '1px solid var(--border, #ddd)',
  fontSize: '0.85rem',
};
const td: React.CSSProperties = {
  padding: '0.3rem 0.5rem',
  fontSize: '0.85rem',
};
